import { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Stack, Text, Paper, Loader, Center, Tabs, Badge } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import CompanyCardHorizontal from "@/components/shared/CompanyCardHorizontal";
import TitleComponent from "@/components/shared/TitleComponent";
import AppHeaderUserActions from "@/components/shared/AppHeaderUserActions";

import { useUserContext } from "@/context/UserContext";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  navyProfileNavTabBorderRadius,
  navyNavTabSegmentBorders,
} from "@/shared/styles/navTabsLayout";
import { useNavTabsStackLayout } from "@/shared/styles/useNavTabsStackLayout";

const MS_PER_DAY = 86_400_000;

/** Days remaining until read-only; computed once per render (no timers / polling). */
function openWindowDeadlineSummary(closesAtIso: string) {
  const end = new Date(closesAtIso).getTime();
  const daysLeft = Math.max(0, Math.ceil((end - Date.now()) / MS_PER_DAY));
  const dateLabel = new Date(closesAtIso).toLocaleDateString(undefined, { dateStyle: "medium" });
  return { daysLeft, dateLabel };
}

export default function CollaborationsPage() {
  const navigate = useNavigate();
  const { hasProfile, refreshCollaborationSidebarCounts } = useUserContext();
  const [collaborations, setCollaborations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>("open");
  const {
    isMobile: isProfileNavMobile,
    tabsListStyles: profileNavTabsListStyles,
    tabsRootStyles: profileNavTabsRootStyles,
  } = useNavTabsStackLayout(2);

  const fetchCollaborations = useCallback(async () => {
    if (hasProfile === null) return;
    setLoading(true);
    try {
      const collaborationsData = await trpc.collaboration.getActive.query();
      setCollaborations(collaborationsData);
      refreshCollaborationSidebarCounts();
    } catch (err) {
      handleTrpcError(err);
      setCollaborations([]);
    } finally {
      setLoading(false);
    }
  }, [hasProfile, refreshCollaborationSidebarCounts]);

  useEffect(() => {
    fetchCollaborations();
  }, [fetchCollaborations]);

  const { openCollaborations, closedCollaborations } = useMemo(() => {
    const open: any[] = [];
    const closed: any[] = [];
    for (const c of collaborations) {
      if (c.readOnlyPhase) closed.push(c);
      else open.push(c);
    }
    return { openCollaborations: open, closedCollaborations: closed };
  }, [collaborations]);

  const handleViewProfile = (companyId: string) => {
    const collaboration = collaborations.find((c) => c.company?.id === companyId);
    if (collaboration) {
      navigate(`/collaborations/${collaboration.id}`);
    }
  };

  const renderClosedPanel = (list: any[], emptyLabel: string) => (
    <Stack gap="md">
      {list.length === 0 ? (
        <Paper p="xl" radius="lg" withBorder>
          <Text c="slate.5" ta="center">
            {emptyLabel}
          </Text>
        </Paper>
      ) : (
        list.map((collaboration) => (
          <CompanyCardHorizontal
            key={collaboration.id}
            company={collaboration.company}
            onShortlistClick={() => {}}
            onViewDetails={handleViewProfile}
            showShortlistButton={false}
            isCollaborationList={true}
          />
        ))
      )}
    </Stack>
  );

  const renderOpenPanel = (list: any[], emptyLabel: string) => (
    <Stack gap="md">
      {list.length === 0 ? (
        <Paper p="xl" radius="lg" withBorder>
          <Text c="slate.5" ta="center">
            {emptyLabel}
          </Text>
        </Paper>
      ) : (
        list.map((collaboration) => {
          const closesAt = collaboration.workspaceClosesAt as string | null | undefined;
          const summary = closesAt ? openWindowDeadlineSummary(closesAt) : null;
          return (
            <CompanyCardHorizontal
              key={collaboration.id}
              company={collaboration.company}
              onShortlistClick={() => {}}
              onViewDetails={handleViewProfile}
              showShortlistButton={false}
              isCollaborationList={true}
              collaborationOpenWindowNotice={summary}
            />
          );
        })
      )}
    </Stack>
  );

  if (loading) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent
          title="Your Collaborations"
          description="Track and manage your ongoing partnerships, and revisit completed collaborations anytime."
          rightSection={<AppHeaderUserActions />}
        />
        <Center py="xl" mih="50vh">
          <Loader size="lg" />
        </Center>
      </Box>
    );
  }

  return (
    <Box mih="100vh" pb={20}>
      <TitleComponent
        title="Your Collaborations"
        description="Track and manage your ongoing partnerships, and revisit completed collaborations anytime."
        rightSection={<AppHeaderUserActions />}
      />

      <Tabs value={activeTab} onChange={setActiveTab} p="md" styles={profileNavTabsRootStyles}>
        <Box w="100%" style={NAV_TABS_FULL_WIDTH_LINE_STYLE}>
          <Tabs.List styles={profileNavTabsListStyles}>
            <Tabs.Tab
              value="open"
              px={{ base: "md", sm: "lg" }}
              py="xs"
              fz={{ base: "xs", sm: "sm" }}
              style={{
                ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                backgroundColor: activeTab === "open" ? "#04305c" : "#ebf0ff",
                color: activeTab === "open" ? "white" : "var(--mantine-color-slate-6)",
                borderRadius: navyProfileNavTabBorderRadius(isProfileNavMobile, "first"),
                ...navyNavTabSegmentBorders(activeTab === "open"),
                fontWeight: activeTab === "open" ? 500 : 400,
                cursor: "pointer",
                minHeight: 44,
              }}
              rightSection={
                <Box
                  w={28}
                  h={28}
                  miw={28}
                  mih={28}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                >
                  <Badge
                    w={24}
                    h={24}
                    p={0}
                    radius="xl"
                    variant="filled"
                    styles={{
                      root: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 24,
                        backgroundColor: activeTab === "open" ? "#ffffff" : "#94a3b8",
                        color: activeTab === "open" ? "#04305c" : "#ffffff",
                        fontWeight: 600,
                        fontSize: 11,
                        border: "none",
                      },
                    }}
                  >
                    {openCollaborations.length}
                  </Badge>
                </Box>
              }
            >
              Active Collaborations
            </Tabs.Tab>

            <Tabs.Tab
              value="closed"
              px={{ base: "md", sm: "lg" }}
              py="xs"
              fz={{ base: "xs", sm: "sm" }}
              style={{
                ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                backgroundColor: activeTab === "closed" ? "#04305c" : "#ebf0ff",
                color: activeTab === "closed" ? "white" : "var(--mantine-color-slate-6)",
                borderRadius: navyProfileNavTabBorderRadius(isProfileNavMobile, "last"),
                ...navyNavTabSegmentBorders(activeTab === "closed"),
                fontWeight: activeTab === "closed" ? 500 : 400,
                cursor: "pointer",
                minHeight: 44,
              }}
              rightSection={
                <Box
                  w={28}
                  h={28}
                  miw={28}
                  mih={28}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                >
                  <Badge
                    w={24}
                    h={24}
                    p={0}
                    radius="xl"
                    variant="filled"
                    styles={{
                      root: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 24,
                        backgroundColor: activeTab === "closed" ? "#ffffff" : "#94a3b8",
                        color: activeTab === "closed" ? "#04305c" : "#ffffff",
                        fontWeight: 600,
                        fontSize: 11,
                        border: "none",
                      },
                    }}
                  >
                    {closedCollaborations.length}
                  </Badge>
                </Box>
              }
            >
              Completed Collaborations
            </Tabs.Tab>
          </Tabs.List>
        </Box>

        <Tabs.Panel value="open" pt="xl">
          {renderOpenPanel(openCollaborations, "No collaborations in the open period yet")}
        </Tabs.Panel>

        <Tabs.Panel value="closed" pt="xl">
          {renderClosedPanel(
            closedCollaborations,
            "No completed collaborations yet — Once your active collaborations are successfully completed, they will appear here for your reference.",
          )}
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}
